import { useState, useEffect } from 'react';
import { createList, updateList } from '../../api/lists';
import { useToastStore } from '../../store/toastStore';
import { useModalStore } from '../../store/modalStore';
import type { ReadingList } from '../../types/models';

interface ListModalProps {
  list?: ReadingList | null;
}

export function ListModal({ list }: ListModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addToast = useToastStore((state) => state.addToast);
  const { closeModal, onSuccess } = useModalStore();

  const isEdit = !!list;

  useEffect(() => {
    if (list) {
      setName(list.name);
      setDescription(list.description || '');
    }
  }, [list]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSubmitting(true);

    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
      };

      if (isEdit && list) {
        await updateList(list.id, data);
        addToast('Collection updated', 'success');
      } else {
        await createList(data);
        addToast('Collection created', 'success');
      }

      if (onSuccess) {
        onSuccess();
      }
      closeModal();
    } catch (err) {
      setError((err as Error).message || 'Failed to save collection');
      console.error('Failed to save collection:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Collection' : 'New Collection'}</h2>
          <button onClick={closeModal} className="modal-close" type="button">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="modal-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  placeholder="Enter collection name"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={closeModal}
              className="btn btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
